import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_SITES_DATA_QUERY } from '../../integrations/allkort/graphql/gql/Sites';
import { Site, SitesDataResponse } from '../../integrations/allkort/graphql/interface/Sites';

const SITES_PER_PAGE = 50;
const MAX_SITES = 1000;

/**
 * Custom hook for fetching ALL sites with automatic pagination
 * Will fetch up to MAX_SITES (1000) sites by following pagination cursors
 * Uses cache-first policy since sites rarely change
 * 
 * Usage:
 * const { sites, loading, error } = useGetAllSites();
 * 
 * @returns Object with sites array, loading state, and error
 */
export const useGetAllSites = () => {
	const [allSites, setAllSites] = useState<Site[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isComplete, setIsComplete] = useState(false);

	// Create stable variables object
	const variables = useMemo(() => ({
		first: SITES_PER_PAGE,
		after: currentCursor,
		before: null,
		last: null
	}), [currentCursor]);

	const { data, loading, error } = useQuery<SitesDataResponse>(
		GET_SITES_DATA_QUERY,
		{
			variables,
			fetchPolicy: 'cache-first', // Sites don't change often
			skip: isComplete,
		}
	);

	useEffect(() => {
		if (!data?.sites) return;

		const { nodes, pageInfo } = data.sites;

		if (nodes && nodes.length > 0) {
			setAllSites(prev => {
				// If this is the first fetch (no cursor), replace all sites
				if (!currentCursor) {
					return nodes;
				}
				// Otherwise append new sites, avoiding duplicates
				const existingIds = new Set(prev.map(s => s.id));
				const newSites = nodes.filter(s => !existingIds.has(s.id));
				return [...prev, ...newSites];
			});
		}

		// Check if we should fetch more
		if (pageInfo.hasNextPage && allSites.length < MAX_SITES) {
			setCurrentCursor(pageInfo.endCursor);
		} else {
			setIsComplete(true);
		}
	}, [data, currentCursor]);

	// Calculate if we're still loading (either Apollo loading or we have more to fetch)
	const isLoading = loading || (!isComplete && allSites.length === 0);

	return {
		sites: allSites,
		loading: isLoading,
		error,
		// Indicates if all pages have been fetched
		isComplete,
		// Total count of sites fetched so far
		count: allSites.length,
	};
};
